import * as React from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import { compose } from "redux";
import { withTheme } from "styled-components";
import { List, Record, Set } from "immutable";

import {
  BOOKING_SUMMARY,
  BOOKING_SUMMARY_EXTRAS,
} from "@config/testIds/booking";
import { MINIMUM_HEIGHT_TO_SHOW_MOBILE_BOOKING_SUMMARY } from "@config/consts";
import { getPriceSubpartTestId } from "@config/testIds/priceSubparts";

import {
  CleaningProduct,
  CLEANING_FREQUENCY,
  EXTRA_SERVICES_BOOKING,
  PricePlan,
  Task,
} from "@typings/booking";
import { WEEKDAYS } from "@typings/globals";
import { LOADING_STATE } from "@typings/reduxThunkActions";
import { TYPE_OF_SUBPARTS } from "@typings/priceSubparts";

import {
  HomeHouseLine,
  ClockTime,
  RefreshRotate,
  CalendarScheduleLines,
} from "@ui/Assets/MyIcons";
import { SidebarSummaryTotal, BoxDivider, PriceSubparts } from "@ui/Molecules";
import { ITheme } from "@ui/themes/ThemeInterface";
import { Margins, TestWrapper } from "@ui/Atoms";
import { MobileBookingSummary } from "@ui/Organisms/MobileBookingSummary";

import { IntlDateFormat, intlFormatDate } from "@services/IntlFormatDate";
import { intlNumberFormatCurrency } from "@services/IntlNumberFormatCurrency";
import { kebabToSnake } from "@services/KebabToSnake";
import slugifyFacade from "@services/SlugifyFacade";
import { formatAmount } from "@services/FormatAmount";
import { formatB2CNonStandardFrequencyShiftsToHumanReadable } from "@services/FormatNonStandardFrequencyShiftsToHumanReadable";
import { stringifyFalsies } from "@services/StringifyFalsies";

import { useConfig } from "@hooks/useConfig";
import { useResize } from "@hooks/useResize";
import { useTranslate } from "@hooks/useTranslate";
import { useScrollPosition, SCROLL_DIR } from "@hooks/useScrollPosition";
import { useActiveElement } from "@hooks/useActiveElement";
import { useBreakpoint } from "@hooks/useBreakpoint";
import { useFeatureToggle } from "@hooks/useFeatureToggle";
import { useVoucherManager } from "@hooks/useVoucherManager";

import {
  getPricing,
  getPricingLoadingState,
  getSelectedCleaningFrequency,
  getTotalCleaningTime as getTotalCleaningTimeSelector,
  getSelectedTasksList,
  getTask,
  getSelectedPricePlan,
  getSelectedPricePlanName,
  getHasServiceVouchers,
  getNonStandardFrequencyShifts,
  getCleaningProducts,
  getSelectedCleaningProductsIds,
  getTotalCleaningTimeMoreOften,
  getHasUrssaf,
  getPriceWillChange,
  getExecutionDate,
} from "@app/redux/booking/booking.selectors";
import { resetVoucher } from "@app/redux/booking/booking.actions";
import { IStore } from "@app/redux/reducers";
import { BookingPricing } from "@app/redux/booking/booking.typings";

import { getBookingExtraServiceTranslation } from "../../Booking.helpers";

import {
  SummaryWrapper,
  SummaryListStyled,
  ExtraItem,
  HomeCleaningType,
  HomeCleaningLabel,
  HomeCleaningPriceWrapper,
  HomeCleaningPrice,
  HomeCleaningPriceCaption,
  DesktopSummaryCleaningProducts,
  MobileSummaryCleaningProducts,
  MobileSummaryHomeCleaningPriceWrapper,
  StyledBox,
  StyledPriceSubpartsList,
  PricePerHour,
  PricePerHourSkeleton,
  PricePerHourSkeletonWrapper,
  MobilePeriodicityContainer,
  SummaryListItem,
} from "./BookingSummary.helpers";

interface BookingSummaryProps {
  theme: ITheme;
  withHeader: boolean;
  isDesktop: boolean;

  pricing: Record<BookingPricing>;
  pricingLoadingState: LOADING_STATE;
  frequency: CLEANING_FREQUENCY;
  cleaningTime: number;
  cleaningTimeMoreOften: number;
  extraServices: List<Task>;
  homeCleaningTask: Record<Task>;
  pricePlan: Record<PricePlan>;
  pricePlanName: string;
  cleaningProducts: List<Record<CleaningProduct>>;
  selectedCleaningProductsIds: Set<string>;
}

const BookingSummaryComponent = (
  props: BookingSummaryProps,
): React.ReactElement | null => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const { windowHeight } = useResize();
  const voucherManager = useVoucherManager();

  const handleVoucherReset = () => {
    voucherManager.removeStoredDeeplinkVoucher();
    voucherManager.removeStoredVoucher();
    dispatch(resetVoucher());
  };

  const servicesList = props.extraServices.filter(
    item => item.service !== EXTRA_SERVICES_BOOKING.HOME_CLEANING,
  );
  const hasHomeCleaning = !!props.homeCleaningTask.get("duration");

  const nonStandardFrequencyShifts = useSelector(getNonStandardFrequencyShifts);
  const hasUrssaf = useSelector(getHasUrssaf);
  const priceWillChange = useSelector(getPriceWillChange);
  const subpartsData = useSelector(getPricing).get("discountsToDisplay");
  const executionDate = useSelector(getExecutionDate);

  const { isFocusedOnTextInputOrTextarea } = useActiveElement();
  const { desktop, desktopLarge } = useBreakpoint("v1");
  const { scrollDir } = useScrollPosition();

  const cleaningProductsFeatureEnabled = useFeatureToggle(
    "cleaning_products_sale",
  );
  const serviceVouchersEnabled = useFeatureToggle("service_voucher");
  const isPricePerHourEnabled = useFeatureToggle(
    "pwa_home_cleaning_price_per_hour",
  );

  const isDesktop = desktop || desktopLarge;
  const isMoreOftenFrequency =
    props.frequency === CLEANING_FREQUENCY.MORE_OFTEN;
  const isBookingSummaryVisible =
    isDesktop ||
    (!isDesktop &&
      windowHeight > MINIMUM_HEIGHT_TO_SHOW_MOBILE_BOOKING_SUMMARY &&
      !isFocusedOnTextInputOrTextarea);
  const isPriceChanging =
    priceWillChange ||
    [LOADING_STATE.IDLE, LOADING_STATE.LOADING].includes(
      props.pricingLoadingState,
    );
  const isSubtotalSectionVisible = React.useMemo(
    () => !!subpartsData.size,
    [subpartsData.size],
  );

  const extraBookingSummaryCaptionFromConfig = useConfig<string | undefined>([
    "booking",
    "extraBookingSummary",
    "caption",
  ]);

  const basePricePerHour = React.useMemo(
    () =>
      intlNumberFormatCurrency(
        props.pricing.get("basePricePerHour"),
        props.pricing.get("currency"),
      ),
    [props.pricing.get("basePricePerHour"), props.pricing.get("currency")],
  );

  const getCleaningTimeTranslation = (cleaningTime: number) => {
    if (cleaningTime > 0 && cleaningTime <= 1) {
      return translate("batmaid_pwa_generic.hour").toLowerCase();
    }
    return translate("batmaid_pwa_generic.hours").toLowerCase();
  };

  const getSummaryPrice = React.useCallback((): number => {
    if (
      cleaningProductsFeatureEnabled &&
      props.pricing.get("totalCheckoutPrice") !== null
    ) {
      return props.pricing.get("totalCheckoutPrice") as number;
    }

    return props.pricing.get("displayedPriceForFirstBooking");
  }, [
    cleaningProductsFeatureEnabled,
    props.pricing.get("totalCheckoutPrice"),
    props.pricing.get("displayedPriceForFirstBooking"),
  ]);

  const getSummaryOverridePrice = React.useCallback((): undefined | string => {
    if (serviceVouchersEnabled && !!props.pricing.get("totalServiceVouchers")) {
      return translate("batmaid_pwa_booking.total_service_vouchers", {
        value: props.pricing.get("totalServiceVouchers") || "",
      });
    }

    return;
  }, [serviceVouchersEnabled, props.pricing.get("totalServiceVouchers")]);

  const getSummaryTooltip = React.useCallback((): undefined | string => {
    return translate("batmaid_pwa_booking.price_tooltip");
  }, []);

  const getSummaryCaption = React.useCallback((): undefined | string => {
    // (per week)
    if (isMoreOftenFrequency) {
      return translate("batmaid_pwa_generic.sidebar_per_week");
    }

    // (per cleaning)
    if (!props.selectedCleaningProductsIds.size) {
      return translate("batmaid_pwa_booking.sidebar_per_cleaning");
    }

    return;
  }, [isMoreOftenFrequency, props.selectedCleaningProductsIds]);

  const getSummaryDescription = React.useCallback((): undefined | string => {
    if (!extraBookingSummaryCaptionFromConfig) return;

    if (hasUrssaf) {
      return translate("batmaid_pwa_urssaf.booking_sidebar_disclaimer");
    }

    return translate(stringifyFalsies(extraBookingSummaryCaptionFromConfig));
  }, [hasUrssaf, extraBookingSummaryCaptionFromConfig]);

  const nonStandardFrequencyShiftsTranslations = {
    [WEEKDAYS.MON]: translate("batmaid_pwa_generic.day_short_1"),
    [WEEKDAYS.TUE]: translate("batmaid_pwa_generic.day_short_2"),
    [WEEKDAYS.WED]: translate("batmaid_pwa_generic.day_short_3"),
    [WEEKDAYS.THU]: translate("batmaid_pwa_generic.day_short_4"),
    [WEEKDAYS.FRI]: translate("batmaid_pwa_generic.day_short_5"),
    [WEEKDAYS.SAT]: translate("batmaid_pwa_generic.day_short_6"),

    hoursText: translate("batmaid_pwa_generic.hours"),
    hourText: translate("batmaid_pwa_generic.hour"),
  };

  const nonStandardFrequencyShiftsList =
    formatB2CNonStandardFrequencyShiftsToHumanReadable(
      nonStandardFrequencyShifts,
      nonStandardFrequencyShiftsTranslations,
    );

  const formattedExecutionDate = executionDate
    ? intlFormatDate(
        new Date(executionDate),
        isMoreOftenFrequency
          ? IntlDateFormat.weekdayLongFullDate
          : IntlDateFormat.full,
      )
    : null;

  if (!isBookingSummaryVisible) {
    return null;
  }

  // MOBILE VERSION
  if (!props.isDesktop) {
    return (
      <MobileBookingSummary.Summary
        testId={BOOKING_SUMMARY.WRAPPER}
        testIds={{
          price: BOOKING_SUMMARY.PRICE,
          tooltip: BOOKING_SUMMARY.TOOLTIP,
          caption: BOOKING_SUMMARY.CAPTION,
          description: BOOKING_SUMMARY.DESCRIPTION,
        }}
        price={getSummaryPrice()}
        currency={props.pricing.get("currency")}
        overridePrice={getSummaryOverridePrice()}
        tooltip={getSummaryTooltip()}
        caption={getSummaryCaption()}
        description={getSummaryDescription()}
        translations={{
          totalText: translate("batmaid_pwa_generic.total"),
        }}
        isLoading={isPriceChanging}
      >
        <React.Fragment>
          <MobileBookingSummary.ChildWrapper>
            <MobileBookingSummary.List>
              {/* Periodicity */}
              <MobileBookingSummary.ListItem testId={BOOKING_SUMMARY.FREQUENCY}>
                <RefreshRotate width={props.theme.icons.sizes.base_x3} />
                <MobilePeriodicityContainer>
                  <div>
                    {!isMoreOftenFrequency
                      ? translate(
                          `batmaid_pwa_generic.periodicity_${props.frequency}`,
                        )
                      : translate("batmaid_pwa_generic.times_a_week", {
                          number: nonStandardFrequencyShiftsList.length,
                        })}

                    {isMoreOftenFrequency &&
                      nonStandardFrequencyShiftsList.map((shift, index) => (
                        <div key={slugifyFacade(shift) + index}>{shift}</div>
                      ))}
                  </div>

                  {isPricePerHourEnabled && (
                    <PricePerHour>
                      {isPriceChanging && (
                        <PricePerHourSkeletonWrapper>
                          <PricePerHourSkeleton noGutter />
                        </PricePerHourSkeletonWrapper>
                      )}

                      <div>
                        {basePricePerHour}/
                        {translate("batmaid_pwa_generic.hours_short")}
                      </div>
                    </PricePerHour>
                  )}
                </MobilePeriodicityContainer>
              </MobileBookingSummary.ListItem>

              {/* Cleaning type */}
              {hasHomeCleaning && (
                <MobileBookingSummary.ListItem
                  testId={BOOKING_SUMMARY.CLEANING_TYPE}
                >
                  <HomeHouseLine width={props.theme.icons.sizes.base_x3} />

                  <div>
                    {/* Cleaning Price */}
                    {cleaningProductsFeatureEnabled && (
                      <MobileSummaryHomeCleaningPriceWrapper
                        hasExtras={!!servicesList.size}
                      >
                        <HomeCleaningPrice>
                          <strong>
                            {formatAmount(
                              "",
                              props.pricing.get("firstCleaningPrice"),
                            )}
                          </strong>
                          <HomeCleaningPriceCaption>
                            /
                            {isMoreOftenFrequency
                              ? translate("batmaid_pwa_generic.week")
                              : translate("batmaid_pwa_generic.cleaning")}
                          </HomeCleaningPriceCaption>
                        </HomeCleaningPrice>
                      </MobileSummaryHomeCleaningPriceWrapper>
                    )}

                    {/* Label */}
                    {translate("batmaid_pwa_booking.home_cleaning")}

                    {/* Extras */}
                    {!!servicesList.size &&
                      servicesList.map(item => {
                        const testIdKey = kebabToSnake(
                          item.service,
                        ) as keyof typeof BOOKING_SUMMARY_EXTRAS;

                        return (
                          <React.Fragment key={item.service}>
                            <TestWrapper
                              testId={BOOKING_SUMMARY_EXTRAS[testIdKey]}
                            >
                              <div>
                                +{" "}
                                {translate(
                                  getBookingExtraServiceTranslation(
                                    item.service,
                                  ),
                                )}
                              </div>
                            </TestWrapper>
                          </React.Fragment>
                        );
                      })}
                  </div>
                </MobileBookingSummary.ListItem>
              )}

              {/* Duration */}
              <MobileBookingSummary.ListItem testId={BOOKING_SUMMARY.DURATION}>
                <ClockTime width={props.theme.icons.sizes.base_x3} />

                <div>
                  {isMoreOftenFrequency
                    ? props.cleaningTimeMoreOften
                    : props.cleaningTime}{" "}
                  {translate(
                    "batmaid_pwa_booking.sidebar_hours_of_home_cleaning",
                  )}
                </div>
              </MobileBookingSummary.ListItem>

              {/* Execution Date */}
              {!!formattedExecutionDate && (
                <MobileBookingSummary.ListItem
                  testId={BOOKING_SUMMARY.DATE_AND_TIME}
                >
                  <CalendarScheduleLines
                    width={props.theme.icons.sizes.base_x3}
                  />

                  {formattedExecutionDate}
                </MobileBookingSummary.ListItem>
              )}
            </MobileBookingSummary.List>
          </MobileBookingSummary.ChildWrapper>

          {/* Cleaning Products */}
          {cleaningProductsFeatureEnabled &&
            !!props.selectedCleaningProductsIds?.size && (
              <MobileBookingSummary.ChildWrapper>
                <MobileSummaryCleaningProducts
                  testId={BOOKING_SUMMARY.CLEANING_PRODUCTS}
                  cleaningProducts={props.cleaningProducts}
                  selectedProductsIds={props.selectedCleaningProductsIds}
                  planHasProductsSpecialPrice={false}
                  translations={{
                    oneTimeLabelText: translate("batmaid_pwa_generic.one_time"),
                  }}
                />
              </MobileBookingSummary.ChildWrapper>
            )}

          {isSubtotalSectionVisible && (
            <MobileBookingSummary.ChildWrapper>
              <PriceSubparts.List>
                <React.Fragment>
                  {subpartsData.map((subpart, index) => (
                    <PriceSubparts.Item
                      key={`subpart-${subpart.type.toLocaleLowerCase()}-${index}`}
                      testId={getPriceSubpartTestId(subpart.type)}
                      variant={
                        subpart.type === TYPE_OF_SUBPARTS.VOUCHER
                          ? "voucher"
                          : "default"
                      }
                      price={subpart.price}
                      currency={subpart.currency}
                      description={subpart.description}
                      tooltip={subpart.tooltip}
                      onVoucherRemove={handleVoucherReset}
                    >
                      {subpart.label}
                    </PriceSubparts.Item>
                  ))}
                </React.Fragment>
              </PriceSubparts.List>
            </MobileBookingSummary.ChildWrapper>
          )}
        </React.Fragment>
      </MobileBookingSummary.Summary>
    );
  }

  // DESKTOP VERSION
  return (
    <StyledBox
      testId={BOOKING_SUMMARY.WRAPPER}
      variant="smallPadding"
      isMoved={scrollDir === SCROLL_DIR.DEFAULT || scrollDir === SCROLL_DIR.UP}
      header={
        props.withHeader ? translate("batmaid_pwa_booking.sidebar_summary") : ""
      }
    >
      <SummaryWrapper>
        <SummaryListStyled>
          {/* Periodicity */}
          <TestWrapper testId={BOOKING_SUMMARY.FREQUENCY}>
            <SummaryListItem>
              <div>
                <RefreshRotate width={props.theme.icons.sizes.base_x3} />
                {!isMoreOftenFrequency
                  ? translate(
                      `batmaid_pwa_generic.periodicity_${props.frequency}`,
                    )
                  : translate("batmaid_pwa_generic.times_a_week", {
                      number: nonStandardFrequencyShiftsList.length,
                    })}
                {isMoreOftenFrequency &&
                  nonStandardFrequencyShiftsList.map((shift, index) => (
                    <ExtraItem key={slugifyFacade(shift) + index}>
                      {shift}
                    </ExtraItem>
                  ))}
              </div>

              {isPricePerHourEnabled && (
                <PricePerHour>
                  {isPriceChanging && (
                    <PricePerHourSkeletonWrapper>
                      <PricePerHourSkeleton noGutter />
                    </PricePerHourSkeletonWrapper>
                  )}

                  <div>
                    {basePricePerHour}/
                    {translate("batmaid_pwa_generic.hours_short")}
                  </div>
                </PricePerHour>
              )}
            </SummaryListItem>
          </TestWrapper>

          {/* Cleaning type */}
          {hasHomeCleaning && (
            <TestWrapper testId={BOOKING_SUMMARY.CLEANING_TYPE}>
              <HomeCleaningType>
                <HomeCleaningLabel>
                  <HomeHouseLine width={props.theme.icons.sizes.base_x3} />
                  {translate("batmaid_pwa_booking.home_cleaning")}
                </HomeCleaningLabel>
                {cleaningProductsFeatureEnabled && (
                  <HomeCleaningPriceWrapper>
                    <HomeCleaningPrice>
                      <strong>
                        {formatAmount(
                          "",
                          props.pricing.get("firstCleaningPrice"),
                        )}
                      </strong>
                      <HomeCleaningPriceCaption>
                        /
                        {isMoreOftenFrequency
                          ? translate("batmaid_pwa_generic.week")
                          : translate("batmaid_pwa_generic.cleaning")}
                      </HomeCleaningPriceCaption>
                    </HomeCleaningPrice>
                  </HomeCleaningPriceWrapper>
                )}
              </HomeCleaningType>
            </TestWrapper>
          )}

          {/* Extras */}
          {!!servicesList.size && (
            <li style={{ display: hasHomeCleaning ? "block" : "flex" }}>
              {hasHomeCleaning && (
                <Margins xs={[null, null, "base_x4", null]}>
                  <div>
                    {servicesList.map(item => {
                      const testIdKey = kebabToSnake(
                        item.service,
                      ) as keyof typeof BOOKING_SUMMARY_EXTRAS;
                      return (
                        <Margins key={item.service}>
                          <TestWrapper
                            testId={BOOKING_SUMMARY_EXTRAS[testIdKey]}
                          >
                            <div>
                              +{" "}
                              {translate(
                                getBookingExtraServiceTranslation(item.service),
                              )}
                            </div>
                          </TestWrapper>
                        </Margins>
                      );
                    })}
                  </div>
                </Margins>
              )}
            </li>
          )}

          <React.Fragment>
            {/* Duration */}
            <TestWrapper testId={BOOKING_SUMMARY.DURATION}>
              <li data-hj-allow>
                <div>
                  <ClockTime width={props.theme.icons.sizes.base_x3} />
                  {isMoreOftenFrequency
                    ? props.cleaningTimeMoreOften
                    : props.cleaningTime}{" "}
                  {hasHomeCleaning &&
                    translate(
                      "batmaid_pwa_booking.sidebar_hours_of_home_cleaning",
                    )}
                  {!hasHomeCleaning && (
                    <span>
                      {getCleaningTimeTranslation(props.cleaningTime)}
                    </span>
                  )}
                </div>
              </li>
            </TestWrapper>

            {/* Execution Date */}
            {!!formattedExecutionDate && (
              <TestWrapper testId={BOOKING_SUMMARY.DATE_AND_TIME}>
                <li>
                  <div>
                    {props.isDesktop && (
                      <CalendarScheduleLines
                        width={props.theme.icons.sizes.base_x3}
                      />
                    )}
                    {formattedExecutionDate}
                  </div>
                </li>
              </TestWrapper>
            )}
          </React.Fragment>
        </SummaryListStyled>

        {cleaningProductsFeatureEnabled &&
          !!props.selectedCleaningProductsIds?.size && (
            <DesktopSummaryCleaningProducts
              testId={BOOKING_SUMMARY.CLEANING_PRODUCTS}
              cleaningProducts={props.cleaningProducts}
              selectedProductsIds={props.selectedCleaningProductsIds}
              planHasProductsSpecialPrice={false}
              translations={{
                oneTimeLabelText: translate("batmaid_pwa_generic.one_time"),
              }}
            />
          )}

        <BoxDivider withFullWidth />

        {isSubtotalSectionVisible && (
          <StyledPriceSubpartsList>
            <React.Fragment>
              {subpartsData.map((subpart, index) => (
                <PriceSubparts.Item
                  key={`subpart-${subpart.type.toLocaleLowerCase()}-${index}`}
                  testId={getPriceSubpartTestId(subpart.type)}
                  variant={
                    subpart.type === TYPE_OF_SUBPARTS.VOUCHER
                      ? "voucher"
                      : "default"
                  }
                  price={subpart.price}
                  currency={subpart.currency}
                  description={subpart.description}
                  tooltip={subpart.tooltip}
                  onVoucherRemove={handleVoucherReset}
                >
                  {subpart.label}
                </PriceSubparts.Item>
              ))}
            </React.Fragment>
          </StyledPriceSubpartsList>
        )}

        <SidebarSummaryTotal
          testIds={{
            price: BOOKING_SUMMARY.PRICE,
            tooltip: BOOKING_SUMMARY.TOOLTIP,
            caption: BOOKING_SUMMARY.CAPTION,
            description: BOOKING_SUMMARY.DESCRIPTION,
          }}
          price={getSummaryPrice()}
          currency={props.pricing.get("currency")}
          overridePrice={getSummaryOverridePrice()}
          tooltip={getSummaryTooltip()}
          caption={getSummaryCaption()}
          description={getSummaryDescription()}
          translations={{
            totalText: translate("batmaid_pwa_booking.sidebar_total"),
          }}
          isLoading={isPriceChanging}
          isBorderVisible={isSubtotalSectionVisible}
        />
      </SummaryWrapper>
    </StyledBox>
  );
};

const mapState = (store: IStore) => ({
  pricing: getPricing(store),
  pricingLoadingState: getPricingLoadingState(store),
  frequency: getSelectedCleaningFrequency(store),
  pricePlan: getSelectedPricePlan(store),
  pricePlanName: getSelectedPricePlanName(store),
  cleaningTime: getTotalCleaningTimeSelector(
    store,
    getHasServiceVouchers(store) ? [EXTRA_SERVICES_BOOKING.IRONING] : [],
  ),
  cleaningTimeMoreOften: getTotalCleaningTimeMoreOften(store),
  extraServices: getSelectedTasksList(store),
  homeCleaningTask: getTask(store, EXTRA_SERVICES_BOOKING.HOME_CLEANING),
  cleaningProducts: getCleaningProducts(store),
  selectedCleaningProductsIds: getSelectedCleaningProductsIds(store),
});

export const BookingSummary = compose(
  withTheme,
  connect(mapState),
)(BookingSummaryComponent);
